import { treaty } from "@elysiajs/eden";

import type { App } from "../server/app";
import { ApiError } from "./http";

/**
 * End-to-end typed API client. `App` is the Elysia server type (imported as a
 * TYPE only — no server code reaches the browser bundle), so request paths,
 * params, and response shapes are inferred from the server. This is what lets us
 * drop hand-mirrored DTOs: the client can't drift from the server contract.
 *
 * Same-origin in the browser, so cookies (the session) ride along automatically.
 */
const domain =
  typeof window !== "undefined"
    ? window.location.origin
    : "http://localhost:3000";

export const api = treaty<App>(domain);

/**
 * Unwrap an Eden treaty call: return the typed success body, or throw a typed
 * `ApiError` on any non-2xx (so the global QueryCache 401 handler still works).
 * `data` is the server's inferred 2xx response — no hand-written DTO.
 */
export async function unwrap<T>(
  call: Promise<{
    data: T | null;
    error: { status: number; value: unknown } | null;
  }>,
): Promise<T> {
  const { data, error } = await call;
  if (error) {
    const message =
      error.value && typeof error.value === "object" && "error" in error.value
        ? String((error.value as { error: unknown }).error)
        : `Request failed (${error.status})`;
    throw new ApiError(error.status, message);
  }
  return data as T;
}
